import React, { ComponentProps, useState, useReducer } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import styled from '@emotion/styled';
import { produce } from 'immer';
import { flatMap } from 'lodash';
import { matchSorter } from 'match-sorter';

import { Box, Button, COLORS, SkeletonLoader, Text, UnstyledButton } from '@clutter/clean';

import { Currency, Pluralize } from '@shared/components/helpers';
import { createTypedReducer } from '@shared/utils/reducers';

import { Fallback } from '@portal/components/helpers/fallback';
import { ItemCard } from '@portal/components/helpers/inventory/item_card';
import { SearchInput } from '@portal/components/helpers/search_input';
import { Tabs } from '@portal/components/helpers/tabs';
import { PricingModalToggle, SERVICE_FEE_AMOUNT } from '@portal/components/orders/disposal_confirmation/pricing_modal';
import { useStoragePricingInfo } from '@portal/components/orders/disposal_confirmation/use_storage_pricing_info';
import { ordersURL } from '@portal/config/routes';
import {
  CustomerItemFragment,
  DisposalConfirmationQuery,
  OrderTypeEnum,
  RateGroupKindEnum,
  Status,
  useAttachedDisposalConfirmationMutation,
  useDisposalConfirmationQuery,
  useDisposalItemPricingQuery,
} from '@portal/schema';

const Card: React.FC<{ children: React.ReactNode } & Pick<ComponentProps<typeof Box.Flex>, 'margin'>> = ({
  children,
  ...props
}) => (
  <Box.Flex
    background={COLORS.dust}
    border={`1px solid ${COLORS.sunshine}`}
    padding="20px"
    flexDirection="column"
    gap="16px"
    borderRadius="4px"
    {...props}
  >
    {children}
  </Box.Flex>
);

const JumboButton = styled(Button)`
  height: unset;
  padding: 24px 100px;
`;

const Header = ({ orderEnded, freeOfCharge }: { orderEnded: boolean; freeOfCharge: boolean }) => (
  <>
    <Text.Title size="large">Disposal Confirmation</Text.Title>
    <Card>
      {freeOfCharge ? (
        <>
          <div>
            We are very sorry that the item(s) below were damaged while in our care. To help make things right, Clutter
            is offering to dispose of them free of charge.
          </div>
          <div>
            <b>
              Please review and confirm below to avoid items going back into your storage account where fees will
              continue to apply.
            </b>
          </div>
        </>
      ) : (
        <>
          <div>
            <b>Clutter intends to dispose of the items shown below</b>. If there are any items below you would rather
            put in storage, unselect them to have them added to your storage account.{' '}
            {!orderEnded && <>If you want an item removed entirely and returned to you now, talk to your movers.</>}
          </div>
          <div>
            <b>If you do not confirm below, all items will be added to your storage account.</b>
          </div>
        </>
      )}
    </Card>
  </>
);

type State = {
  disposalUngroupedItemsAndGroups: CustomerItemFragment[];
  storedUngroupedItemsAndGroups: CustomerItemFragment[];
};

const actions = {
  moveToStorage(state: State, payload: { uuid: string }) {
    return produce(state, (draft) => {
      const movedItem = state.disposalUngroupedItemsAndGroups.findIndex((item) => item.uuid === payload.uuid);
      draft.storedUngroupedItemsAndGroups.push(draft.disposalUngroupedItemsAndGroups[movedItem]);
      draft.disposalUngroupedItemsAndGroups.splice(movedItem, 1);
    });
  },
  moveToDisposal(state: State, payload: { uuid: string }) {
    return produce(state, (draft) => {
      const movedItem = state.storedUngroupedItemsAndGroups.findIndex((item) => item.uuid === payload.uuid);
      draft.disposalUngroupedItemsAndGroups.push(draft.storedUngroupedItemsAndGroups[movedItem]);
      draft.storedUngroupedItemsAndGroups.splice(movedItem, 1);
    });
  },
};

const reducer = createTypedReducer(actions);

const SORT_OPTIONS = { keys: ['name', 'category.name', 'contents'] };

const Inner = ({
  data: { order },
  signedDisposalID,
}: {
  data: DisposalConfirmationQuery;
  signedDisposalID: string;
}) => {
  if (!order.parent) throw new Error('Invalid disposal confirmation without parent!');

  const account = order.account;
  const currentStorageSubscription = account.currentStorageSubscription;
  // Field Return dispose children bill at $0; portal shows the damaged/free confirmation variant.
  const freeOfCharge = order.parent.type === OrderTypeEnum.Return;

  const [activeTab, setActiveTab] = useState<'disposal' | 'stored'>('disposal');
  const [searchInput, setSearchInput] = useState('');
  const [{ disposalUngroupedItemsAndGroups, storedUngroupedItemsAndGroups }, dispatch] = useReducer(reducer, {
    disposalUngroupedItemsAndGroups: order.customerItems,
    storedUngroupedItemsAndGroups: order.associatedPickup?.customerItems ?? [],
  });
  const allDisposalItems = flatMap(
    disposalUngroupedItemsAndGroups.map((itemOrGroup) =>
      itemOrGroup.__typename === 'Item' ? [itemOrGroup] : itemOrGroup.items,
    ),
  );
  const allStorageItems = flatMap(
    storedUngroupedItemsAndGroups.map((itemOrGroup) =>
      itemOrGroup.__typename === 'Item' ? [itemOrGroup] : itemOrGroup.items,
    ),
  );

  const history = useHistory();

  const [confirm, { data: mutationData, loading }] = useAttachedDisposalConfirmationMutation({
    onCompleted(result) {
      if (result.disposalConfirm?.status === Status.Ok) {
        if (result.disposalConfirm.signingURL) {
          window.location.assign(result.disposalConfirm.signingURL);
        } else {
          history.push(ordersURL());
        }
      }
    },
  });
  const { data: pricingData } = useDisposalItemPricingQuery({
    variables: {
      itemIds: allDisposalItems.map((item) => item.id).sort(),
      signedOrderID: signedDisposalID,
      signedOrderPurpose: 'DisposalConfirmation',
    },
  });
  const error = mutationData?.disposalConfirm?.error;

  const rateGroupKind =
    // If the customer didn't add storage ahead of time we default to Mover
    order.parent?.type === OrderTypeEnum.Move && !order.associatedPickup
      ? RateGroupKindEnum.Mover
      : (account.rateGroup?.kind ?? RateGroupKindEnum.Mover);

  // Return-parent free disposals keep dual-membered items on the Return; do not upsell a storage plan.
  const pricingInfo = useStoragePricingInfo({
    newCuft: freeOfCharge ? 0 : storedUngroupedItemsAndGroups.reduce((acc, cur) => acc + (cur.cuft ?? 0), 0),
    currentPlanCuft: currentStorageSubscription?.cubicFootage ?? undefined,
    signedParentOrderID: order.parent.disposalConfirmationSignedOrderId,
    signedParentOrderPurpose: 'DisposalConfirmation',
    rateGroupKind,
  });

  const filteredDisposalItems = searchInput
    ? matchSorter(disposalUngroupedItemsAndGroups, searchInput, SORT_OPTIONS)
    : disposalUngroupedItemsAndGroups;
  const filteredStoredItems = searchInput
    ? matchSorter(storedUngroupedItemsAndGroups, searchInput, SORT_OPTIONS)
    : storedUngroupedItemsAndGroups;
  const items = activeTab === 'disposal' ? filteredDisposalItems : filteredStoredItems;
  const itemCostTotal = pricingData?.disposalItemPricing.reduce((acc, cur) => acc + cur.amount, 0);
  const total = freeOfCharge ? 0 : itemCostTotal !== undefined ? itemCostTotal + SERVICE_FEE_AMOUNT : undefined;
  const keptTabLabel = freeOfCharge ? 'Kept Items' : 'Stored Items';
  const keepButtonText = freeOfCharge ? 'Keep Item' : 'Store Item';
  // Field End Appointment stamps `ended` on the Return parent, not the Disposal child.
  // Lock Keep/Dispose once either the disposal or its parent appointment has ended.
  const selectionLocked = !!order.ended || !!order.parent.ended;

  const itemCard = (item: CustomerItemFragment) => (
    <ItemCard.Container key={item.uuid} data-test-id={`item-${item.uuid}`}>
      {activeTab === 'disposal' ? (
        <ItemCard.Content
          item={item}
          selected={true}
          selectedText={keepButtonText}
          showConditionNote={false}
          showPrimaryAction={!selectionLocked}
        />
      ) : (
        <ItemCard.Content
          item={item}
          selected={false}
          unselectedText="Dispose Item"
          showConditionNote={false}
          showPrimaryAction={!selectionLocked}
        />
      )}
    </ItemCard.Container>
  );

  return (
    <Box.Flex gap="24px" flexDirection="column">
      <Header orderEnded={selectionLocked} freeOfCharge={freeOfCharge} />
      <SearchInput value={searchInput} onChange={setSearchInput} />
      <Tabs>
        <Tabs.Tab onClick={() => setActiveTab('disposal')} active={activeTab === 'disposal'}>
          Disposal Items ({filteredDisposalItems.length} / {disposalUngroupedItemsAndGroups.length})
        </Tabs.Tab>
        <Tabs.Tab onClick={() => setActiveTab('stored')} active={activeTab === 'stored'}>
          {keptTabLabel} ({filteredStoredItems.length} / {storedUngroupedItemsAndGroups.length})
        </Tabs.Tab>
      </Tabs>
      <Box.Flex flexWrap="wrap" gap="16px">
        {items.map((item) =>
          selectionLocked ? (
            <React.Fragment key={item.uuid}>{itemCard(item)}</React.Fragment>
          ) : (
            <UnstyledButton
              key={item.uuid}
              onClick={() => {
                if (activeTab === 'disposal') {
                  dispatch({ type: 'moveToStorage', payload: { uuid: item.uuid } });
                } else {
                  dispatch({ type: 'moveToDisposal', payload: { uuid: item.uuid } });
                }
              }}
            >
              {itemCard(item)}
            </UnstyledButton>
          ),
        )}
      </Box.Flex>
      <Box margin="32px 0 0">
        <Text.Body>
          {disposalUngroupedItemsAndGroups.length > 0 && (
            <p>
              Clutter will dispose of the{' '}
              <b>
                <Pluralize
                  count={disposalUngroupedItemsAndGroups.length}
                  singular="selected item"
                  plural="selected items"
                />
              </b>
              {freeOfCharge ? (
                <> free of charge.</>
              ) : (
                <>
                  {' '}
                  and you will be billed for{' '}
                  <Fallback
                    value={total}
                    delayMs={0}
                    loader={
                      <SkeletonLoader width="50px" height="17px" display="inline-flex" position="relative" top="2px" />
                    }
                  >
                    {total !== undefined && <Currency value={total} />}
                  </Fallback>{' '}
                  (<PricingModalToggle itemizedPricing={pricingData?.disposalItemPricing ?? []} />){'. '}
                </>
              )}
            </p>
          )}
          {storedUngroupedItemsAndGroups.length > 0 && (
            <p>
              Your{' '}
              <b>
                <Pluralize
                  count={storedUngroupedItemsAndGroups.length}
                  singular="remaining item"
                  plural="remaining items"
                />
              </b>{' '}
              {freeOfCharge ? 'will not be disposed.' : 'will be put in storage.'}{' '}
            </p>
          )}
          {!freeOfCharge && pricingInfo?.changeDescription}
        </Text.Body>
      </Box>
      <Box.Flex justifyContent="center" flexDirection="column" gap="16px">
        <Text.Body color={COLORS.toucan}>{error ?? '\u00A0'} </Text.Body>
        <JumboButton
          loading={loading}
          onClick={() =>
            confirm({
              variables: {
                signedDisposalID: signedDisposalID,
                disposalItemIDs: allDisposalItems.map((item) => item.id),
                storageItemIDs: allStorageItems.map((item) => item.id),
                storagePlanConfig:
                  !freeOfCharge && pricingInfo
                    ? {
                        planID: pricingInfo.newPlan.id,
                        quoteID: pricingInfo.quoteId,
                      }
                    : undefined,
              },
            })
          }
        >
          Confirm & Sign
        </JumboButton>
      </Box.Flex>
    </Box.Flex>
  );
};

export const DisposalConfirmation = () => {
  const { orderID } = useParams<{ orderID: string }>();
  const { data } = useDisposalConfirmationQuery({
    variables: { signedOrderID: orderID, signedOrderPurpose: 'DisposalConfirmation' },
  });

  if (!data)
    return (
      <Box.Flex gap="24px" flexDirection="column">
        <Header orderEnded={false} freeOfCharge={false} />
        <SearchInput />
        <Tabs>
          <Tabs.Tab active={true}>Disposal Items</Tabs.Tab>
          <Tabs.Tab active={false}>Stored Items</Tabs.Tab>
        </Tabs>
        <Box.Flex flexWrap="wrap" gap="16px">
          <SkeletonLoader width="208px" height="286px" />
          <SkeletonLoader width="208px" height="286px" />
          <SkeletonLoader width="208px" height="286px" />
        </Box.Flex>
      </Box.Flex>
    );

  return <Inner data={data} signedDisposalID={orderID} />;
};
